import axios from "axios";
import { showToast } from "../utils/toast";

const apiUrl = "/api";

// ─── Global error interceptor ─────────────────────────────────────────────────
// Catches all API errors and shows a user-friendly toast message.
// Individual catch blocks still receive the error for any local handling.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      const requestUrl = error?.config?.url || "";
      const isAdminRoute = requestUrl.includes("/admin/");

      if (!isAdminRoute) {
        // Expired / invalid customer token — clear session and redirect to home
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        localStorage.removeItem("userName");
        localStorage.removeItem("userMobile");
        localStorage.removeItem("userAddress");
        localStorage.removeItem("deliveryAddress");

        if (!window._sessionExpiredShown) {
          window._sessionExpiredShown = true;
          showToast(
            "Your session has expired. Please log in again to continue.",
            "warning",
            5000
          );
          setTimeout(() => {
            window._sessionExpiredShown = false;
            window.location.href = "/";
          }, 2000);
        }
      }
      // Admin 401s (wrong password, expired admin token) are handled by the
      // admin components themselves — no redirect, no toast here.

    } else if (!error.response) {
      // Network error — no response received (offline, server unreachable)
      showToast(
        "Unable to connect. Please check your internet connection and try again.",
        "warning"
      );

    } else if (status >= 500) {
      // Server-side error — use backend message if available, else generic fallback
      const serverMsg = error.response?.data?.message;
      showToast(
        serverMsg || "Something went wrong on our end. Please try again in a moment.",
        "error"
      );

    } else if (status === 403) {
      showToast("You do not have permission to perform this action.", "error");

    } else if (status === 404) {
      // 404s are often expected (e.g. cart empty) — don't show a toast by default.
      // Individual callers can handle these themselves.

    }
    // For all other 4xx: let the individual component handle it (e.g. form validation errors)

    return Promise.reject(error);
  }
);

export const razorPay_Order = async (totals, localOrderId) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.post(
      `${apiUrl}/createRazorpayOrder`,
      { totals, localOrderId },
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const razorPay_verifyPayment = async (
  razorpayIds,
  cartItm,
  localOrdId,
  amount,
) => {
  const token = localStorage.getItem("token");
  const rOrderId = razorpayIds.razorpay_order_id;
  const rPaymentId = razorpayIds.razorpay_payment_id;
  const rSignature = razorpayIds.razorpay_signature;

  try {
    const response = await axios.post(
      `${apiUrl}/verifyPayment`,
      {
        rOrderId,
        rPaymentId,
        rSignature,
        cartItems: cartItm,
        localOrderId: localOrdId,
        amount,
      },
      {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getUserShipments = async (userId) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(`${apiUrl}/getUserShipments`, {
      params: { userId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const getUserOrders = async () => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${apiUrl}/getUserOrders`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response;
};

export const cancelUserOrder = async (orderId) => {
  const token = localStorage.getItem("token");
  const response = await axios.post(
    `${apiUrl}/cancelOrder`,
    { orderId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response;
};

export const getCategories = async (lng) => {
  try {
    const response = await axios.get(`${apiUrl}/getCategories`, {
      params: { lng: lng },
    });
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const getNavMenu = async (lng) => {
  try {
    const response = await axios.get(`${apiUrl}/getNavMenu`, {
      params: { lng: lng },
    });
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const getProducts = async (params) => {
  try {
    const response = await axios.get(`${apiUrl}/getProducts`, {
      //params: { lng: lng },
      params: params,
    });
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const getProDetail = async (productId) => {
  try {
    const response = await axios.get(`${apiUrl}/getProDetail`, {
      params: { productId: productId },
    });
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const getCompanyInfo = async (lng) => {
  try {
    const response = await axios.get(`${apiUrl}/getCompanyInfo`, {
      params: { lng: lng },
    });
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const sendOtp = async (phone) => {
  try {
    const response = await axios.post(
      `${apiUrl}/sendOtp`,
      {
        phone: phone,
      },
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const resendOtp = async (phone) => {
  try {
    const response = await axios.post(
      `${apiUrl}/resendOtp`,
      { phone },
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const verifyOtp = async (phone, enteredOtp) => {
  try {
    const response = await axios.post(
      `${apiUrl}/verifyOtp`,
      {
        phone: phone,
        enteredOtp: enteredOtp,
      },
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const saveUserAddress = async (formData, userId) => {
  const token = localStorage.getItem("token");

  try {
    const response = await axios.post(
      `${apiUrl}/saveUserAddress`,
      { formData: formData, userId: userId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true, // Optional: if your backend uses cookies/sessions
      },
    );
    return response;
  } catch (error) {
    console.error("Error saving user address:", error);
    return "Error";
  }
};

export const setDefaultAdd = async (addressId, userId) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.post(
      `${apiUrl}/setDefaultAddress`,
      { addressId: addressId, userId: userId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    console.error("Error saving user address:", error);
    return "Error";
  }
};

export const getUserProfileData = async (mobileNo) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(`${apiUrl}/getUserProfile`, {
      params: { mobile_no: mobileNo },
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return "Error";
  }
};

export const updateUserProfileData = async (mobileNo, name, email) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.post(
      `${apiUrl}/updateUserProfile`,
      { mobile_no: mobileNo, name: name, email: email },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    console.error("Error updating user profile:", error);
    return "Error";
  }
};

export const uploadUserImageData = async (userId, file) => {
  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("user_id", userId);
  formData.append("user_img", file);
  try {
    const response = await axios.post(`${apiUrl}/uploadUserImage`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    console.error("Error uploading user image:", error);
    return "Error";
  }
};

export const addToCartData = async (formData, userId) => {
  const token = localStorage.getItem("token");

  try {
    const response = await axios.post(
      `${apiUrl}/addToCart`,
      { formData: formData, userId: userId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true, // Optional: if your backend uses cookies/sessions
      },
    );
    return response;
  } catch (error) {
    console.error("Error saving user add cart item:", error);
    return "Error";
  }
};

export const getCartData = async (userId, lng) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.get(`${apiUrl}/getCartData`, {
      params: { userId, lng },
      headers: { Authorization: `Bearer ${token}` },
    });
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const deleteFromCartData = async (formData, userId) => {
  const token = localStorage.getItem("token");

  try {
    const response = await axios.post(
      `${apiUrl}/deleteFromCart`,
      { formData: formData, userId: userId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    console.error("Error deleting add cart item:", error);
    return "Error";
  }
};

export const saveOrdMaster = async (userId, totals, discount, addressId, shippingCharge, shippingProvider) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.post(
      `${apiUrl}/saveOrderMaster`,
      {
        userId,
        totals,
        discount,
        addressId,
        shippingCharge,
        shippingProvider,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    console.error("Error saving order:", error);
    return { error: true, message: error?.response?.data?.message || "Failed to create order." };
  }
};

export const saveOrder = async (formData, userId, localOrderId) => {
  const token = localStorage.getItem("token");

  try {
    const response = await axios.post(
      `${apiUrl}/saveOrder`,
      { formData: formData, userId: userId, localOrderId: localOrderId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true, // Optional: if your backend uses cookies/sessions
      },
    );
    return response;
  } catch (error) {
    console.error("Error saving order:", error);
    return "Error";
  }
};

export const getDiscount = async (couponCode, orderTotal) => {
  try {
    const response = await axios.get(`${apiUrl}/getDiscount`, {
      params: { couponCode: couponCode, orderTotal: orderTotal },
    });
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const adminLogin = async (username, password) => {
  try {
    const response = await axios.post(
      `${apiUrl}/admin/login`,
      {
        username: username,
        password: password,
      },
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const verifyAdminToken = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/verify`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminCategories = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/categories`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const createCategory = async (formData) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.post(
      `${apiUrl}/admin/categories`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const updateCategory = async (id, formData) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.put(
      `${apiUrl}/admin/categories/${id}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const deleteCategory = async (id) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.delete(`${apiUrl}/admin/categories/${id}`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminUsers = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/users`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const createAdminUser = async (payload) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.post(`${apiUrl}/admin/users`, payload, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const changeAdminPassword = async (id, payload) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.put(
      `${apiUrl}/admin/users/${id}/password`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminSellers = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/sellers`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminProducts = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/products`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminProductDetail = async (id) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/products/${id}`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const createAdminProduct = async (formData) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.post(`${apiUrl}/admin/products`, formData, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
        "Content-Type": "multipart/form-data",
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const updateAdminProduct = async (id, formData) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.put(
      `${apiUrl}/admin/products/${id}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const deleteAdminProduct = async (id) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.delete(`${apiUrl}/admin/products/${id}`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const exportAdminAvailQty = async (sellerId) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(
      `${apiUrl}/admin/products/export-avail-qty`,
      {
        params: { seller_id: sellerId },
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
        responseType: "blob",
        withCredentials: true,
      },
    );
    return response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const importAdminAvailQty = async (formData) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.post(
      `${apiUrl}/admin/products/import-avail-qty`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminOrders = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/orders`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminOrderDetails = async (orderId) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(
      `${apiUrl}/admin/orders/${orderId}/details`,
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminOrderCustomer = async (userId, addressId) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(
      `${apiUrl}/admin/orders/customer/${userId}`,
      {
        params: addressId ? { addressId } : {},
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminCustomers = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/customers`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminCustomerAddresses = async (userId) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(
      `${apiUrl}/admin/customers/${userId}/addresses`,
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminCategorySales = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/dashboard/category-sales`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminParentCategories = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/parent-categories`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const createAdminParentCategory = async (formData) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.post(`${apiUrl}/admin/parent-categories`, formData, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
        "Content-Type": "multipart/form-data",
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const updateAdminParentCategory = async (pcId, formData) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.put(
      `${apiUrl}/admin/parent-categories/${pcId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const deleteAdminParentCategory = async (pcId) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.delete(
      `${apiUrl}/admin/parent-categories/${pcId}`,
      {
        headers: { Authorization: `Bearer ${adminToken}` },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const deleteAdminProductImage = async (id, imageId) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.delete(
      `${apiUrl}/admin/products/${id}/images/${imageId}`,
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

// ── Discount Coupons (admin) ─────────────────────────────────────────────────
export const getAdminDiscounts = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/discounts`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const createAdminDiscount = async (payload) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.post(`${apiUrl}/admin/discounts`, payload, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const updateAdminDiscount = async (id, payload) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.put(`${apiUrl}/admin/discounts/${id}`, payload, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const deleteAdminDiscount = async (id) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.delete(`${apiUrl}/admin/discounts/${id}`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

// ── GST Settings (admin) ─────────────────────────────────────────────────────
export const getAdminGstRate = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/gst`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const updateAdminGstRate = async (gst_percentage) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.put(
      `${apiUrl}/admin/gst`,
      { gst_percentage },
      {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

// ── Shipments (admin) ────────────────────────────────────────────────────────
export const getAdminShipments = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/shipments`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const createAdminShipment = async (payload) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.post(`${apiUrl}/admin/shipments`, payload, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const updateAdminShipment = async (id, payload) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.put(`${apiUrl}/admin/shipments/${id}`, payload, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

// ── Public GST rate (for Cart) ───────────────────────────────────────────────
export const getPublicGstRate = async () => {
  try {
    const response = await axios.get(`${apiUrl}/gstRate`);
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const getParentCategoryName = async (pcId, lng) => {
  try {
    const response = await axios.get(`${apiUrl}/parentCategory/${pcId}`, {
      params: { lng },
    });
    return await response;
  } catch (error) {
    return "Error";
  }
};

// ── Shipping Options (admin) ──────────────────────────────────────────────────
export const getShippingProviders = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/shipping-options/providers`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const getAdminShippingOptions = async () => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.get(`${apiUrl}/admin/shipping-options`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const createAdminShippingOption = async (payload) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.post(`${apiUrl}/admin/shipping-options`, payload, {
      headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const updateAdminShippingOption = async (id, payload) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.put(`${apiUrl}/admin/shipping-options/${id}`, payload, {
      headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const deleteAdminShippingOption = async (id) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.delete(`${apiUrl}/admin/shipping-options/${id}`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

export const setDefaultAdminShippingOption = async (id) => {
  const adminToken = localStorage.getItem("adminToken");
  try {
    const response = await axios.put(`${apiUrl}/admin/shipping-options/${id}/set-default`, {}, {
      headers: { Authorization: `Bearer ${adminToken}` },
      withCredentials: true,
    });
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};

// ── Shipping (public) ─────────────────────────────────────────────────────────
export const getPublicShippingOption = async () => {
  try {
    const response = await axios.get(`${apiUrl}/shippingOption`);
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const calculateShippingCharge = async (cartItems, pincode) => {
  try {
    const response = await axios.post(`${apiUrl}/calculateShipping`, { cartItems, pincode });
    return await response;
  } catch (error) {
    return "Error";
  }
};

export const razorPay_paymentFailed = async (localOrderId) => {
  const token = localStorage.getItem("token");
  try {
    const response = await axios.post(
      `${apiUrl}/paymentFailed`,
      { params: { localOrderId } },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
    return await response;
  } catch (error) {
    return error.response || "Error";
  }
};
